import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, Check } from 'lucide-react';
import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import Checkbox from './Checkbox';
import FilterChip from './FilterChip';
import Select from './Select';
import { trapPanelTab, useAnchoredPanel } from './use-anchored-panel';
import {
    DATE_PRESETS,
    VALUE_LIST_DISPLAY_CAP,
    conditionFromInput,
    inputFromCondition,
    operatorsFor,
    parseNumberInput,
    valuesDraft,
    type ColumnFilter,
    type ConditionInput,
    type FilterCondition,
    type DistinctValues,
    type FilterType,
} from './data-table-filter';

export interface ColumnFilterPopoverProps {
    /** The header cell. The panel opens under it. */
    anchor: HTMLElement;
    columnLabel: string;
    type: FilterType;
    value: ColumnFilter | undefined;
    distinct: DistinctValues;
    sort: 'asc' | 'desc' | null;
    canSort: boolean;
    numeric: boolean;
    onSort: (desc: boolean) => void;
    onApply: (next: ColumnFilter | undefined) => void;
    onClose: () => void;
}

/** The attribute that marks a funnel. A pointer-down on it does not close the panel. */
export const FILTER_TRIGGER_ATTRIBUTE = 'data-column-filter-trigger';

const PANEL_WIDTH = 264;

const INPUT_CLASS = cn(
    'h-7 w-full min-w-0 rounded-button border border-[rgb(var(--color-divider))]',
    'bg-[rgb(var(--color-surface))] px-2 text-annotation text-[rgb(var(--color-text-primary))]',
    'placeholder:text-[rgb(var(--color-text-tertiary))]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
    'pointer-coarse:h-8',
);
const INVALID_RING = 'ring-2 ring-[rgb(var(--color-rag-red))]';

function defaultOp(type: FilterType): string {
    if (type === 'number') return 'eq';
    if (type === 'date') return 'on';
    return 'contains';
}

const EMPTY_INPUT = (type: FilterType): ConditionInput => ({ op: defaultOp(type), a: '', b: '' });

function isBare(op: string): boolean {
    return op === 'blank' || op === 'notBlank' || op === 'preset';
}

const SORT_LABELS: Record<'text' | 'number' | 'date', [string, string]> = {
    text: ['Sort A to Z', 'Sort Z to A'],
    number: ['Smallest to largest', 'Largest to smallest'],
    date: ['Oldest to newest', 'Newest to oldest'],
};

interface ConditionInputsProps {
    type: FilterType;
    input: ConditionInput;
    onChange: (next: ConditionInput) => void;
}

/** The value inputs of one condition: none, one, or two for "Between". */
function ConditionInputs({ type, input, onChange }: ConditionInputsProps) {
    const numericInput = type === 'number';
    const dateInput = type === 'date';
    if (isBare(input.op)) return null;
    const invalid = (raw: string) =>
        numericInput && raw.trim() !== '' && parseNumberInput(raw) === null;
    const field = (key: 'a' | 'b') => {
        const bad = invalid(input[key]);
        const label = key === 'a' ? (input.op === 'between' ? 'From' : 'Value') : 'To';
        return (
            <input
                type={dateInput ? 'date' : 'text'}
                inputMode={numericInput ? 'decimal' : undefined}
                value={input[key]}
                aria-label={label}
                aria-invalid={bad || undefined}
                placeholder={label}
                onChange={(e) => onChange({ ...input, [key]: e.target.value })}
                className={cn(
                    INPUT_CLASS,
                    (numericInput || dateInput) && 'font-mono tabular-nums',
                    bad && INVALID_RING,
                )}
            />
        );
    };
    return (
        <div className="flex items-center gap-1">
            {field('a')}
            {input.op === 'between' && (
                <>
                    <span className="text-caption text-[rgb(var(--color-text-tertiary))]">and</span>
                    {field('b')}
                </>
            )}
        </div>
    );
}

interface ConditionRowProps extends ConditionInputsProps {
    label: string;
}

/** One operator select with its value inputs, for the two rows of a custom filter. */
function ConditionRow({ type, input, onChange, label }: ConditionRowProps) {
    return (
        <div className="flex flex-col gap-1">
            <Select
                size="sm"
                aria-label={`${label} condition`}
                wrapperClassName="flex w-full"
                value={input.op}
                onChange={(e) => onChange({ op: e.target.value, a: input.a, b: input.b })}
            >
                {operatorsFor(type).map((o) => (
                    <option key={o.op} value={o.op}>
                        {o.label}
                    </option>
                ))}
            </Select>
            <ConditionInputs type={type} input={input} onChange={onChange} />
        </div>
    );
}

// The panel for one column, in the layout of the Tercero grid: the sort rows,
// then two tabs. "Values" ticks values from a list; on a number column it
// takes a range from one number to another. "Text Filters" (or "Number
// Filters", "Date Filters") takes one condition, or two joined by And or Or
// under "Custom Filter". Filters on several columns stack.
//
// The panel holds a staged draft. Nothing reaches the table until the reader
// presses Apply, so a half-typed rule never hides rows.
//
// The panel portals to `document.body`. The table card is `overflow-hidden`
// and would clip it. It sits at `position: fixed`. The `top` and `left` are the
// one inline style, as with the `overlay` of `HoverPopover`, because the
// coordinates come from a measurement at runtime.

type Tab = 'values' | 'filters';

/** The value of the filter-type select that opens the two-condition form. */
const CUSTOM = 'custom';

const FILTERS_TAB_LABEL: Record<FilterType, string> = {
    text: 'Text Filters',
    enum: 'Text Filters',
    number: 'Number Filters',
    date: 'Date Filters',
};

// A range on the Values tab of a number column. One bound alone is a
// "from" or a "to" rule.
function rangeCondition(from: string, to: string): FilterCondition | null {
    const a = from.trim() === '' ? null : parseNumberInput(from);
    const b = to.trim() === '' ? null : parseNumberInput(to);
    if ((from.trim() !== '' && a === null) || (to.trim() !== '' && b === null)) return null;
    if (a !== null && b !== null) return { type: 'number', op: 'between', value: a, to: b };
    if (a !== null) return { type: 'number', op: 'gte', value: a };
    if (b !== null) return { type: 'number', op: 'lte', value: b };
    return null;
}

// A saved filter that the Values tab of a number column can show again.
function isRange(value: ColumnFilter | undefined, type: FilterType): boolean {
    return (
        type === 'number' &&
        value?.kind === 'conditions' &&
        !value.second &&
        value.first.op === 'between'
    );
}

export default function ColumnFilterPopover({
    anchor,
    columnLabel,
    type,
    value,
    distinct,
    sort,
    canSort,
    numeric,
    onSort,
    onApply,
    onClose,
}: ColumnFilterPopoverProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // An enum column has a closed list of values, so it has no filters tab.
    const hasFiltersTab = type !== 'enum';
    const [tab, setTab] = useState<Tab>(() =>
        value?.kind === 'conditions' && hasFiltersTab && !isRange(value, type)
            ? 'filters'
            : 'values',
    );

    // ---- Draft state: the filters tab --------------------------------------
    const [custom, setCustom] = useState(value?.kind === 'conditions' && Boolean(value.second));
    const [first, setFirst] = useState<ConditionInput>(() =>
        value?.kind === 'conditions' && !isRange(value, type)
            ? inputFromCondition(value.first)
            : EMPTY_INPUT(type),
    );
    const [second, setSecond] = useState<ConditionInput>(() =>
        value?.kind === 'conditions' && value.second
            ? inputFromCondition(value.second)
            : EMPTY_INPUT(type),
    );
    const [join, setJoin] = useState<'and' | 'or'>(
        value?.kind === 'conditions' ? (value.join ?? 'and') : 'and',
    );

    // ---- Draft state: the values tab ---------------------------------------
    const [ticked, setTicked] = useState<ReadonlySet<string>>(() =>
        value?.kind === 'values'
            ? new Set(value.values)
            : new Set(distinct.entries.map((e) => e.key)),
    );
    const [blanksTicked, setBlanksTicked] = useState(
        value?.kind === 'values' ? value.includeBlanks : true,
    );
    const [search, setSearch] = useState('');
    const [range, setRange] = useState(() => {
        if (isRange(value, type) && value?.kind === 'conditions') {
            const c = inputFromCondition(value.first);
            return { from: c.a, to: c.b };
        }
        return { from: '', to: '' };
    });
    const numberRange = type === 'number';

    const firstCondition = useMemo(() => conditionFromInput(type, first), [type, first]);
    const secondCondition = useMemo(() => conditionFromInput(type, second), [type, second]);
    const rangeDraft = useMemo(() => rangeCondition(range.from, range.to), [range]);
    const rangeInvalid =
        (range.from.trim() !== '' && parseNumberInput(range.from) === null) ||
        (range.to.trim() !== '' && parseNumberInput(range.to) === null);

    // The search narrows the value list. With a search, Apply keeps only the
    // ticked matches, as in Excel (see `valuesDraft`).
    const needle = search.trim().toLowerCase();
    const matches = useMemo(
        () => distinct.entries.filter((e) => e.label.toLowerCase().includes(needle)),
        [distinct.entries, needle],
    );
    const blanksVisible = distinct.blanks > 0 && '(blanks)'.includes(needle);

    // ---- The filter that Apply would send ---------------------------------
    const draft = useMemo<{ complete: boolean; filter: ColumnFilter | undefined }>(() => {
        if (tab === 'filters') {
            if (!firstCondition) return { complete: false, filter: undefined };
            if (!custom)
                return { complete: true, filter: { kind: 'conditions', first: firstCondition } };
            if (!secondCondition) return { complete: false, filter: undefined };
            return {
                complete: true,
                filter: {
                    kind: 'conditions',
                    first: firstCondition,
                    join,
                    second: secondCondition,
                },
            };
        }
        if (numberRange) {
            // An empty range is no filter.
            if (rangeInvalid) return { complete: false, filter: undefined };
            return {
                complete: true,
                filter: rangeDraft ? { kind: 'conditions', first: rangeDraft } : undefined,
            };
        }
        return { complete: true, filter: valuesDraft(distinct, ticked, blanksTicked, search) };
    }, [
        tab,
        custom,
        numberRange,
        rangeDraft,
        rangeInvalid,
        search,
        firstCondition,
        secondCondition,
        join,
        distinct,
        ticked,
        blanksTicked,
    ]);

    // ---- Focus on open ----------------------------------------------------
    useEffect(() => {
        const target = panelRef.current?.querySelector<HTMLElement>('[data-autofocus]');
        target?.focus();
        // Open once. Later edits must not pull focus back.
    }, []);

    const restoreFocus = () => {
        anchor.querySelector<HTMLElement>(`[${FILTER_TRIGGER_ATTRIBUTE}]`)?.focus();
    };
    const closeWithFocus = () => {
        restoreFocus();
        onClose();
    };

    // ---- Position, and close on outside press, scroll and resize ----------
    const pos = useAnchoredPanel({
        anchor,
        panelRef,
        width: PANEL_WIDTH,
        triggerAttribute: FILTER_TRIGGER_ATTRIBUTE,
        onClose,
    });

    // ---- Actions ----------------------------------------------------------
    const apply = () => {
        if (!draft.complete) return;
        onApply(draft.filter);
        closeWithFocus();
    };
    const clear = () => {
        onApply(undefined);
        closeWithFocus();
    };
    const sortAndClose = (desc: boolean) => {
        onSort(desc);
        closeWithFocus();
    };

    const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        // Alt+ArrowUp goes to the parent page. Inside the panel it must not.
        if (e.altKey && e.key === 'ArrowUp') e.stopPropagation();
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closeWithFocus();
            return;
        }
        if (e.key === 'Enter') {
            const el = e.target as HTMLElement;
            if (el.tagName === 'INPUT' && (el as HTMLInputElement).type !== 'checkbox') {
                e.preventDefault();
                apply();
            }
            return;
        }
        trapPanelTab(e, panelRef.current);
    };

    const onListKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const boxes = Array.from(
            listRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? [],
        );
        const at = boxes.indexOf(document.activeElement as HTMLInputElement);
        if (at < 0) return;
        e.preventDefault();
        boxes[
            Math.max(0, Math.min(boxes.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))
        ]?.focus();
    };

    // ---- Value list -------------------------------------------------------
    const shown = matches.slice(0, VALUE_LIST_DISPLAY_CAP);
    const visibleTicked =
        matches.filter((e) => ticked.has(e.key)).length + (blanksVisible && blanksTicked ? 1 : 0);
    const visibleTotal = matches.length + (blanksVisible ? 1 : 0);
    const allVisibleTicked = visibleTotal > 0 && visibleTicked === visibleTotal;
    const someVisibleTicked = visibleTicked > 0 && !allVisibleTicked;

    const toggleKey = (key: string) =>
        setTicked((old) => {
            const next = new Set(old);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    const toggleAllVisible = () => {
        const turnOn = !allVisibleTicked;
        setTicked((old) => {
            const next = new Set(old);
            for (const e of matches) {
                if (turnOn) next.add(e.key);
                else next.delete(e.key);
            }
            return next;
        });
        if (blanksVisible) setBlanksTicked(turnOn);
    };

    const sortKind: 'text' | 'number' | 'date' =
        type === 'date' ? 'date' : type === 'number' || numeric ? 'number' : 'text';
    const [ascLabel, descLabel] = SORT_LABELS[sortKind];

    const sortRow = (desc: boolean, label: string) => {
        const current = sort === (desc ? 'desc' : 'asc');
        return (
            <button
                type="button"
                onClick={() => sortAndClose(desc)}
                className={cn(
                    'flex h-7 w-full items-center gap-2 rounded px-2 text-left pointer-coarse:h-8',
                    'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                    current && 'text-[rgb(var(--color-accent))]',
                )}
            >
                {desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                <span className="flex-1">{label}</span>
                {current && <Check className="h-3 w-3" />}
            </button>
        );
    };

    const presetActive = first.op === 'preset' ? first.preset : undefined;

    const tabButton = (key: Tab, label: string) => (
        <button
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
                'h-7 flex-1 border-b-2 px-2 font-medium pointer-coarse:h-8',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
                tab === key
                    ? 'border-[rgb(var(--color-accent))] text-[rgb(var(--color-text-primary))]'
                    : 'border-transparent text-[rgb(var(--color-text-tertiary))] hover:text-[rgb(var(--color-text-secondary))]',
            )}
        >
            {label}
        </button>
    );

    const valuesTab = numberRange ? (
        <div className="flex flex-col gap-1">
            <span className="text-caption text-[rgb(var(--color-text-tertiary))]">Range</span>
            <div className="flex items-center gap-1">
                <input
                    type="text"
                    inputMode="decimal"
                    value={range.from}
                    aria-label="From"
                    placeholder="From"
                    data-autofocus=""
                    aria-invalid={
                        (range.from.trim() !== '' && parseNumberInput(range.from) === null) ||
                        undefined
                    }
                    onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                    className={cn(
                        INPUT_CLASS,
                        'font-mono tabular-nums',
                        range.from.trim() !== '' &&
                            parseNumberInput(range.from) === null &&
                            INVALID_RING,
                    )}
                />
                <span className="text-caption text-[rgb(var(--color-text-tertiary))]">to</span>
                <input
                    type="text"
                    inputMode="decimal"
                    value={range.to}
                    aria-label="To"
                    placeholder="To"
                    aria-invalid={
                        (range.to.trim() !== '' && parseNumberInput(range.to) === null) || undefined
                    }
                    onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                    className={cn(
                        INPUT_CLASS,
                        'font-mono tabular-nums',
                        range.to.trim() !== '' &&
                            parseNumberInput(range.to) === null &&
                            INVALID_RING,
                    )}
                />
            </div>
        </div>
    ) : (
        <div className="flex flex-col gap-1">
            <input
                type="search"
                value={search}
                placeholder="Search values"
                aria-label="Search values"
                data-autofocus=""
                onChange={(e) => setSearch(e.target.value)}
                className={INPUT_CLASS}
            />
            <div ref={listRef} onKeyDown={onListKeyDown} className="flex flex-col">
                <label className="flex h-6 cursor-pointer items-center gap-2 px-1 pointer-coarse:h-8">
                    <Checkbox
                        checked={allVisibleTicked}
                        indeterminate={someVisibleTicked}
                        onChange={toggleAllVisible}
                        disabled={visibleTotal === 0}
                    />
                    <span>Select all</span>
                </label>
                <div className="max-h-[220px] overflow-y-auto">
                    {blanksVisible && (
                        <label className="flex h-6 cursor-pointer items-center gap-2 px-1 pointer-coarse:h-8">
                            <Checkbox
                                checked={blanksTicked}
                                onChange={() => setBlanksTicked((v) => !v)}
                            />
                            <span className="flex-1 truncate">(Blanks)</span>
                            <span className="font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                                {distinct.blanks.toLocaleString('en-GB')}
                            </span>
                        </label>
                    )}
                    {shown.map((e) => (
                        <label
                            key={e.key}
                            className="flex h-6 cursor-pointer items-center gap-2 px-1 pointer-coarse:h-8"
                        >
                            <Checkbox
                                checked={ticked.has(e.key)}
                                onChange={() => toggleKey(e.key)}
                            />
                            <span className="flex-1 truncate" title={e.label}>
                                {e.label}
                            </span>
                            <span className="font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                                {e.count.toLocaleString('en-GB')}
                            </span>
                        </label>
                    ))}
                    {visibleTotal === 0 && (
                        <p className="px-1 py-1 text-[rgb(var(--color-text-tertiary))]">
                            No matching values
                        </p>
                    )}
                </div>
            </div>
            {(matches.length > VALUE_LIST_DISPLAY_CAP || distinct.truncated) && (
                <p className="px-1 text-caption text-[rgb(var(--color-text-tertiary))]">
                    Showing the first 500 values. Search to find more.
                </p>
            )}
        </div>
    );

    const filtersTab = (
        <div className="flex flex-col gap-1.5">
            {type === 'date' && !custom && (
                <div className="flex flex-wrap gap-1">
                    {DATE_PRESETS.map((p) => (
                        <FilterChip
                            key={p.preset}
                            label={p.label}
                            isSelected={presetActive === p.preset}
                            onClick={() =>
                                setFirst(
                                    presetActive === p.preset
                                        ? EMPTY_INPUT(type)
                                        : { op: 'preset', a: '', b: '', preset: p.preset },
                                )
                            }
                        />
                    ))}
                </div>
            )}
            {/* The filter type, as in Tercero: one operator, or "Custom
                Filter" for two conditions joined by And or Or. */}
            <Select
                size="sm"
                aria-label="Filter type"
                wrapperClassName="flex w-full"
                value={custom ? CUSTOM : first.op === 'preset' ? defaultOp(type) : first.op}
                data-autofocus=""
                onChange={(e) => {
                    const op = e.target.value;
                    if (op === CUSTOM) {
                        setCustom(true);
                        return;
                    }
                    setCustom(false);
                    setFirst({ op, a: first.op === 'preset' ? '' : first.a, b: first.b });
                }}
            >
                {operatorsFor(type).map((o) => (
                    <option key={o.op} value={o.op}>
                        {o.label}
                    </option>
                ))}
                <option value={CUSTOM}>Custom Filter</option>
            </Select>
            {custom ? (
                <>
                    <ConditionRow
                        type={type}
                        input={first.op === 'preset' ? EMPTY_INPUT(type) : first}
                        onChange={setFirst}
                        label="First"
                    />
                    <div className="flex gap-1">
                        <FilterChip
                            label="And"
                            isSelected={join === 'and'}
                            onClick={() => setJoin('and')}
                        />
                        <FilterChip
                            label="Or"
                            isSelected={join === 'or'}
                            onClick={() => setJoin('or')}
                        />
                    </div>
                    <ConditionRow
                        type={type}
                        input={second.op === 'preset' ? EMPTY_INPUT(type) : second}
                        onChange={setSecond}
                        label="Second"
                    />
                </>
            ) : (
                first.op !== 'preset' && (
                    <ConditionInputs type={type} input={first} onChange={setFirst} />
                )
            )}
        </div>
    );

    const panel = (
        <div
            ref={panelRef}
            role="dialog"
            aria-label={`Filter ${columnLabel}`}
            onKeyDown={onPanelKeyDown}
            style={{ top: pos.top, left: pos.left }}
            className={cn(
                'fixed z-[80] flex w-[264px] max-h-[min(480px,calc(100vh-16px))] flex-col',
                // The raised surface and the popover shadow keep the panel apart
                // from the rows under it, in light and in dark.
                'rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-raised))]',
                'text-annotation text-[rgb(var(--color-text-primary))] shadow-[var(--septima-popover-shadow)]',
            )}
        >
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
                {canSort && (
                    <div className="flex flex-col border-b border-[rgb(var(--color-divider))] pb-2">
                        {sortRow(false, ascLabel)}
                        {sortRow(true, descLabel)}
                    </div>
                )}
                {hasFiltersTab && (
                    <div
                        role="tablist"
                        aria-label="Filter by"
                        className="-mx-2 -mt-1 flex border-b border-[rgb(var(--color-divider))] px-2"
                    >
                        {tabButton('values', 'Values')}
                        {tabButton('filters', FILTERS_TAB_LABEL[type])}
                    </div>
                )}
                {tab === 'values' || !hasFiltersTab ? valuesTab : filtersTab}
            </div>

            <div className="flex justify-between border-t border-[rgb(var(--color-divider))] p-2">
                <Button variant="ghost" size="sm" onClick={clear} disabled={value === undefined}>
                    Clear Filter
                </Button>
                <Button variant="primary" size="sm" onClick={apply} disabled={!draft.complete}>
                    Apply
                </Button>
            </div>
        </div>
    );

    return createPortal(panel, document.body);
}
